/**
 * ⚠️ `/min` ET NON LE DÉFAUT. La bibliothèque est celle du produit
 * (`react-phone-number-input`, V1 et back office), mais son entrée par défaut embarque
 * les métadonnées `max`. Mesuré sur le paquet construit : 128 ko gzip sans elle,
 * 183 avec `/min`, 205 avec `/max` — 22 ko gagnés pour un jeu de métadonnées qui
 * formate et valide tous les pays quand même (ce qu'il perd, c'est de distinguer
 * fixe/mobile, dont ce formulaire ne fait rien).
 *
 * Les noms de pays viennent du fichier FRANÇAIS : la liste sortait en anglais, dans
 * un produit français, au milieu d'un formulaire traduit.
 */
import PhoneInput, {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input/min";
import type { Country, FlagProps, Labels } from "react-phone-number-input";
import fr from "react-phone-number-input/locale/fr";
import "react-phone-number-input/style.css";
import {
  COMPLAINTS,
  isEmail,
  isPassword,
  leftToDo,
  type Invalid,
} from "./checks";
import { MULTIPLE, chosen, selection, type Field } from "./forms";
import type { Words } from "./formI18n";
import { useLocale, useT } from "../settings/context";
import { useEffect, useRef, useState } from "react";
import { accepts, formatAttachment, parseAttachment } from "./attachment";
import { UploadError, attachmentUrl, uploadAttachment } from "../api/upload";
import { Prose } from "../ui/Prose";

/**
 * UN CHAMP DU BACK OFFICE, RENDU — et UN SEUL rendu pour tout le tunnel.
 *
 * ⚠️ IL A ÉTÉ EXTRAIT LE JOUR OÙ UN SECOND ÉCRAN EN A EU BESOIN, pas avant. L'étape
 * société sert elle aussi un formulaire du back office : recopié là-bas, ce fichier
 * aurait donné deux rendus du même champ, libres de diverger — et le premier écart
 * aurait été silencieux, parce que chacun s'affiche très bien tout seul. Tout ce qui
 * suit a été payé une fois (le vrai téléphone, la molette du champ Nombre, la réponse
 * à choix en `{ clé: true }`, le verrou qui n'est pas un `disabled`) et n'a pas à
 * l'être deux fois.
 *
 * Ce qu'il ne fait PAS : décider ce qui est verrouillé, ni ce qui est fautif. Le
 * premier dépend de ce que le serveur détient déjà, le second de règles qui se
 * testent (`checks.ts`) — les deux appartiennent à l'écran, qui les lui passe.
 *
 * Displayed strings are French: this is a French product.
 */

/**
 * ── L'INDICATIF, PAS LE CODE ISO ──
 *
 * ⚠️ « FR » N'EST PAS UN INDICATIF. La pastille annonçait le code ISO du pays,
 * devant un champ où l'on tape un NUMÉRO et dont la valeur enregistrée commence
 * par « +33 » : le seul endroit du formulaire où le préfixe aurait pu se lire ne
 * le montrait pas. Le code du pays reste — « +1 » est américain OU canadien —
 * mais l'indicatif passe devant.
 *
 * La table est dressée une fois, sur les métadonnées que `/min` a DÉJÀ chargées
 * pour formater : pas une requête, pas un octet de plus.
 */
const DIALLING_CODES = new Map(
  getCountries().map((c) => [c, `+${getCountryCallingCode(c)}`] as const),
);

function DiallingCode({ country, countryName }: FlagProps) {
  return (
    <span className="PhoneInputCode" title={countryName}>
      <b>{country}</b> {DIALLING_CODES.get(country)}
    </span>
  );
}

/**
 * ⚠️ LE NOM DU PAYS *ET* SON INDICATIF DANS LA LISTE. Elle en compte plus de
 * deux cents, par ordre alphabétique français : qui sait être en « +32 » devait
 * deviner que ça s'écrit « Belgique » pour s'y retrouver.
 */
const COUNTRIES: Labels = {
  ...fr,
  ...Object.fromEntries(
    getCountries().map((c) => [c, `${fr[c] || c} (${DIALLING_CODES.get(c)})`]),
  ),
};

/**
 * ⚠️ LA FRANCE, PUIS SES VOISINS, PUIS LE RESTE. `'...'` est le mot de la
 * bibliothèque pour « tous les autres, par ordre alphabétique » et `'|'` un
 * séparateur. Sans ordre, un salon français ouvre sa liste sur l'Afghanistan.
 */
const ORDER: Array<Country | "|" | "..."> = ["FR", "BE", "CH", "LU", "|", "..."];

/**
 * LA RÉPONSE, TELLE QU'UN CHAMP DE SAISIE PEUT LA PORTER.
 *
 * ⚠️ UN NOMBRE EN BASE N'EST PAS UNE CHAÎNE, et `typeof value === "string"` le rendait
 * donc en case VIDE. L'API range ce qu'on lui donne sans le convertir : un `42` brut
 * écrit par un import ou par un autre client est parfaitement stocké — la
 * documentation du produit prévient d'ailleurs de n'envoyer que des chaînes, PARCE QUE
 * le back office n'affiche pas le reste. Un champ « Nombre » relu ici s'ouvrait donc
 * sur du vide au-dessus d'une réponse qui existait.
 */
const text = (value: unknown) =>
  typeof value === "string"
    ? value
    : typeof value === "number"
      ? String(value)
      : "";

/**
 * LE `type` DE LA SAISIE — ce que le navigateur en fait.
 *
 * ⚠️ `email` POUR LE CHAMP EMAIL — comme la V1 et le back office, qui le posent tous deux
 * sur `model.key === 'email'`. Ce n'est pas décoratif : c'est le clavier à `@` sur mobile,
 * la bonne proposition du gestionnaire de mots de passe, et une première validation native
 * gratuite. Elle ne suffit pas — le navigateur laisse passer « a@b » que le serveur refuse
 * — d'où le contrôle de `verifie` par-dessus.
 *
 * ⚠️ `password` POUR LE MOT DE PASSE, et c'était le manque le plus coûteux : cette clé
 * change le mot de passe DU COMPTE (`forms/updateData` : `md5` puis `UPDATE
 * Authentications`), et le champ sortait en `text` — lisible à l'écran de qui passe
 * derrière, et ignoré du gestionnaire de mots de passe. Voir `isPassword`.
 *
 * ⚠️ `number` POUR « NOMBRE », dont c'est tout l'intérêt : le pavé numérique sur mobile,
 * et un champ qui refuse les lettres. La VALEUR, elle, reste une CHAÎNE —
 * `e.target.value` en est une, et c'est ce que le produit attend : sa propre documentation
 * prévient qu'un nombre brut est bien enregistré mais que le back office ne l'affiche pas,
 * donc que la réponse deviendrait invisible dans la liste des participants.
 */
function typeDeSaisie(field: Field): "number" | "email" | "password" | "text" {
  if (field.component === "input-number") return "number";
  if (isEmail(field)) return "email";
  if (isPassword(field)) return "password";
  return "text";
}

export function FieldRow({
  field,
  value,
  locked,
  error,
  words,
  onChange,
}: {
  field: Field;
  value: unknown;
  /** Non modifiable : il porte déjà une réponse et le back office l'a figé. */
  locked: boolean;
  /** Ce qu'on reproche à la réponse saisie — dit sous le champ concerné. */
  error?: string;
  /** Les libellés dans la langue du participant — voir `funnel/formI18n.ts`. */
  words: Words;
  onChange: (v: unknown) => void;
}) {
  const t = useT();
  const id = `f-${field.id}`;

  /* Un seul reproche, une seule mise en forme : le numéro impossible et l'adresse
     refusée se disent au même endroit, du même ton. `role="alert"` parce qu'il
     apparaît APRÈS coup, en réponse à « Continuer ». */
  const alert = error ? (
    <p className="f__err" id={`${id}-err`} role="alert">
      {/* ⚠️ TRADUIT ICI, PAS DANS `verifie` : ce module-là est pur, donc sans
          langue. `REPROCHES` déclare la clé de chaque phrase ; ce qu'elle ignore,
          un refus venu du serveur, retombe sur lui-même. */}
      {t(COMPLAINTS[error] ?? error, error)}
    </p>
  ) : null;
  const invalid = {
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `${id}-err` : undefined,
  } as const;

  /* La largeur voulue par le back office, en classe. `pleine` — le défaut quand rien
     n'est réglé — donne exactement la mise en page d'avant. Voir `chassis.css`.
     Les décors la reçoivent aussi : un commentaire court n'a pas plus de raison qu'un
     code postal de manger toute une ligne. */
  const width = `f--${field.width}`;
  const rowClass = `f ${width}`;

  /**
   * ⚠️ `readOnly`, PAS `disabled`, pour un champ verrouillé qui porte une valeur.
   * Un champ désactivé est sauté par la tabulation et souvent ignoré des lecteurs
   * d'écran : la réponse déjà enregistrée devient invisible pour qui navigue au
   * clavier, alors que c'est justement ce qu'il faut pouvoir relire avant de
   * continuer. Et un mot le dit, sinon un champ qui refuse la frappe passe pour
   * cassé.
   */
  const marked = locked ? (
    <small className="f__lock">{t("NOT_EDITABLE", 'Non modifiable')}</small>
  ) : null;

  if (field.component === "html") {
    // The organiser's own markup, as they wrote it — same trust the product grants
    // them on its information pages.
    return field.content.trim() ? (
      <Prose
        html={words.decor(field)}
        className={`se-prose ${width}`}
        />
    ) : null;
  }
  if (field.component === "comment") {
    return field.content.trim() ? (
      <p className={`hint ${width}`}>{words.decor(field)}</p>
    ) : null;
  }

  const name = words.field(field);

  const label = (
    <label htmlFor={id}>
      {name}
      {field.required && <i> *</i>}
      {marked}
    </label>
  );

  if (field.component === "select") {
    return (
      <div className={rowClass}>
        {label}
        <select
          id={id}
          required={field.required}
          /* Un select ne connaît pas `readOnly` : là, `disabled` est le seul
             mécanisme, et le mot « Non modifiable » à côté fait le reste. */
          disabled={locked}
          {...invalid}
          /* Une liste est un champ à OPTIONS : sa réponse est `{ clé: true }`, pas la
             clé nue — voir `choisies` / `selection`. */
          value={chosen(value)[0] ?? ""}
          onChange={(e) =>
            onChange(selection(e.target.value ? [e.target.value] : []))
          }
        >
          <option value="">—</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {words.option(field, o)}
            </option>
          ))}
        </select>
        {/* ⚠️ IL MANQUAIT ICI AUSSI, ET LE COMMENTAIRE D'À CÔTÉ AFFIRMAIT LE
            CONTRAIRE. Une liste obligatoire laissée sur « — » est bien jugée fautive
            (`verifie` la range dans `AVEC_OPTIONS`), l'écran met le reproche en état et
            ramène le focus dessus — mais la branche ne le rendait nulle part. Le
            participant voyait « Continuer » ne rien faire, sans un mot, exactement
            comme pour les groupes de choix. */}
        {alert}
      </div>
    );
  }

  if (MULTIPLE.has(field.component) || field.component === "radio") {
    const many = MULTIPLE.has(field.component);
    const keys = chosen(value);

    return (
      <div
        className={rowClass}
        /**
         * ⚠️ UN `id` ET UN FOCUS POSSIBLE, comme les champs de saisie. L'écran ramène le
         * participant sur le champ qu'il reproche (`document.getElementById("f-…")`) —
         * un groupe de choix n'en avait pas, donc le reproche s'écrivait peut-être en
         * dehors de l'écran, sans que rien n'y conduise : « Continuer » semblait ne rien
         * faire. `tabIndex={-1}` le rend focalisable par le programme SANS l'ajouter au
         * parcours du clavier, où il n'a rien à faire : ce sont les options qui se
         * tabulent.
         */
        id={id}
        tabIndex={-1}
        role={many ? "group" : "radiogroup"}
        aria-label={name}
        {...invalid}
      >
        {/* A group of choices is not a `label`-for-one-input: the heading names the
            question, and each option carries its own label. */}
        <span className="f__t">
          {name}
          {field.required && <i> *</i>}
          {marked}
        </span>
        {/**
         * ⚠️ LES CHOIX DANS LEUR PROPRE BOÎTE, et pas directement dans `.f`. Celle-ci
         * est une colonne — l'intitulé, puis les contrôles, puis le reproche — et il
         * FAUT qu'elle le reste : mise en ligne, l'intitulé de la question se
         * retrouverait à côté de la première case. Seuls les choix se répartissent,
         * donc seuls les choix ont besoin d'un conteneur à eux.
         */}
        <div className={field.choicesInColumn ? "fchoix fchoix--col" : "fchoix"}>
          {field.options.map((o) => (
            <label className="fcase" key={o.value}>
              <input
                type={many ? "checkbox" : "radio"}
                name={id}
                value={o.value}
                disabled={locked || o.disabled}
                checked={keys.includes(o.value)}
                onChange={(e) => {
                  if (!many) return onChange(selection([o.value]));
                  const next = new Set(keys);
                  if (e.target.checked) next.add(o.value);
                  else next.delete(o.value);
                  onChange(selection([...next]));
                }}
              />
              <span>{words.option(field, o)}</span>
            </label>
          ))}
        </div>
        {/* ⚠️ IL MANQUAIT ICI. Un reproche visant un groupe de choix était calculé, mis
            en état… et jamais dit : le participant voyait « Continuer » ne rien faire.
            La branche `select` avait le même trou — TOUTE branche qui rend un contrôle
            doit terminer par `{alerte}` et porter `{...fautif}`. */}
        {alert}
      </div>
    );
  }

  /**
   * LE TÉLÉPHONE — celui du produit, pas un `type="tel"`.
   *
   * ⚠️ La V1 ET le back office utilisent `react-phone-number-input` (`defaultCountry
   * "FR"`), donc un indicatif choisi dans une liste et une valeur au format
   * international. Un simple champ texte ici et le même numéro serait stocké
   * « 06 12 … » d'un côté, « +33612… » de l'autre — pour les mêmes participants, dans
   * la même base, avec les SMS du produit au bout.
   *
   * Vide vaut `null`, comme chez elle (`onChange(v || null)`).
   */
  if (field.component === "phone-number") {
    return (
      <div className={rowClass}>
        {label}
        <PhoneInput
          id={id}
          /* ⚠️ `required` COMME DANS TOUTES LES AUTRES BRANCHES DE CETTE FONCTION.
             Il manquait : un téléphone obligatoire au back office était le seul
             champ du formulaire que « Continuer » laissait passer vide. La
             bibliothèque le transmet à son `<input>`, la validation native fait le
             reste — comme pour un champ texte. */
          required={field.required}
          /* ⚠️ PAS `international` : il force la saisie à commencer par « + », et
             « 0612345678 » ressortait « +0612345678 ». La V1 ne le passe pas — on
             tape son numéro comme on l'écrit, et la valeur STOCKÉE est mise au
             format international par la bibliothèque. */
          defaultCountry="FR"
          /* ⚠️ ET PAS D'OPTION « INTERNATIONAL » NON PLUS : la choisir met le champ
             dans l'état que la ligne au-dessus évite — plus de pays sélectionné,
             donc une saisie qui doit commencer par « + ». Mesuré : on y tape son
             numéro entier et RIEN ne s'enregistre, la valeur ne devient jamais un
             E.164. C'était la même trappe, ouverte en haut de la liste.
             Le prix : un numéro déjà en base sans pays (un « +800 » mondial, pas
             un mobile) n'a plus d'option à sa taille et s'affiche sans pastille.
             Il reste lisible — la bibliothèque l'écrit alors en entier, indicatif
             compris — et c'est le seul cas contre une trappe ouverte à tous. */
          addInternationalOption={false}
          labels={COUNTRIES}
          countryOptionsOrder={ORDER}
          /**
           * ⚠️ LE CODE DU PAYS, PAS SON DRAPEAU — et c'est une correction sur la V1,
           * pas une préférence. Laissée à son défaut, la bibliothèque charge chaque
           * drapeau depuis `purecatamphetamine.github.io` : mesuré, une requête vers
           * le GitHub Pages d'un tiers à l'ouverture du formulaire. Sur le wifi d'un
           * salon le drapeau casse, et chaque participant y annonce sa visite. Les
           * embarquer coûtait +105 ko gzip pour une décoration.
           *
           * Ce qui le remplace est l'indicatif — voir `Indicatif`. Composant hissé
           * au module et non écrit ici : la bibliothèque fige le sien au montage
           * (`createCountryIconComponent(props)` dans son constructeur), une
           * fonction recréée à chaque frappe n'y serait jamais reprise.
           */
          flagComponent={DiallingCode}
          readOnly={locked}
          {...invalid}
          value={typeof value === "string" ? value : undefined}
          onChange={(v) => onChange(v ?? null)}
        />
        {alert}
      </div>
    );
  }

  /**
   * « TEXTE SUR PLUSIEURS LIGNES » — un `<textarea>`, pas un `<input>` plus large.
   *
   * ⚠️ TROIS LIGNES, COMME LA V1 (`rows={3}`) : c'est ce qui distingue le champ à
   * l'œil et annonce qu'on peut y écrire un paragraphe. Rendu comme les autres, la
   * question posée pour recueillir un commentaire recevait un mot.
   *
   * Il n'a pas de `type`, mais il a `readOnly` — donc le verrou de l'écran s'y
   * applique tel quel, sans repasser par `disabled` (voir plus haut pourquoi).
   */
  if (field.component === "file") {
    return (
      <div className={rowClass}>
        {label}
        <AttachmentInput
          id={id}
          field={field}
          value={value}
          locked={locked}
          invalid={invalid}
          onChange={onChange}
        />
        {alert}
      </div>
    );
  }

  if (field.component === "textarea") {
    return (
      <div className={rowClass}>
        {label}
        <textarea
          id={id}
          rows={3}
          required={field.required}
          readOnly={locked}
          {...invalid}
          value={text(value)}
          onChange={(e) => onChange(e.target.value)}
        />
        {alert}
      </div>
    );
  }

  return (
    <div className={rowClass}>
      {label}
      <input
        id={id}
        type={typeDeSaisie(field)}
        /**
         * ⚠️ `new-password` ET NON `on` : le champ s'appelle « Nouveau mot de passe », donc
         * le navigateur doit PROPOSER d'en générer un, pas recoller l'ancien par-dessus.
         * Les autres champs gardent le réglage du navigateur : le produit ne sait pas ce
         * qu'une question personnalisée attend.
         */
        autoComplete={isPassword(field) ? "new-password" : undefined}
        required={field.required}
        readOnly={locked}
        {...invalid}
        /**
         * ⚠️ LA MOLETTE SUR UN `type="number"` INCRÉMENTE LE CHAMP. Focalisé, il
         * répond à la roulette : on clique dedans, on fait défiler la page pour
         * atteindre « Continuer », et la réponse a changé en chemin — sans un geste
         * qui ressemble à une saisie, et sans que rien ne le signale. Le remède
         * habituel, et le seul qui n'invente pas de clavier : lâcher le champ.
         */
        onWheel={
          field.component === "input-number"
            ? (e) => e.currentTarget.blur()
            : undefined
        }
        value={text(value)}
        onChange={(e) => onChange(e.target.value)}
      />
      {alert}
    </div>
  );
}

/**
 * CE QU'IL RESTE À FAIRE AVANT QUE LA SAISIE PARTE, posé près du bouton.
 *
 * ⚠️ ICI PLUTÔT QUE TROIS FOIS DANS LES ÉCRANS : la phrase compose une clé, un repli et
 * un nom de champ, et les trois écrans qui portent un formulaire l'affichaient à
 * l'identique. Recopiée, c'est la traduction de l'un des trois qu'on aurait oubliée.
 */
export function Remaining({
  invalid,
  words,
}: {
  /** Tous les fautifs, dans l'ordre du formulaire : la phrase les nomme tous. */
  invalid: ReadonlyArray<Invalid>;
  words: Words;
}) {
  const t = useT();
  const locale = useLocale();
  const sentences = leftToDo(
    invalid.map(({ field, complaint }) => ({
      complaint,
      label: words.field(field),
    })),
    locale,
  );
  return (
    <>{sentences.map(({ key, raw, names }) => t(key, raw, [names])).join(" ")}</>
  );
}

/**
 * ═══ UNE PIÈCE JOINTE — le fichier part au seau PRIVÉ, le champ garde sa clé ═══
 *
 * Le geste est celui du panneau organisateur (`panel/Upload.tsx`) : un `<input
 * type="file">` caché, un bouton qui le déclenche, et l'envoi qui part TOUT DE SUITE.
 * La réponse du champ devient `file:<clé>:<nom>` (voir `funnel/attachment.ts`) et
 * suit le chemin de n'importe quel texte — c'est « Continuer » qui l'enregistre,
 * comme les autres champs, pas cet écran.
 *
 * ⚠️ UN FICHIER TÉLÉVERSÉ PUIS ABANDONNÉ (on quitte sans « Continuer ») reste dans
 * le seau, orphelin. Assumé : une clé que personne ne détient n'est lisible par
 * personne, et c'est le même sort que celui d'un logo remplacé dans le panneau.
 *
 * ⚠️ « OUVRIR » DEMANDE UNE ADRESSE AU CLIC, pas au rendu : elle expire en cinq
 * minutes, et un lien posé dans la page à l'ouverture serait mort au moment de
 * cliquer. La fenêtre s'ouvre AVANT l'appel réseau, sinon le bloqueur de
 * fenêtres surgissantes la refuse (une ouverture doit suivre le geste sans attendre).
 */
function AttachmentInput({
  id,
  field,
  value,
  locked,
  invalid,
  onChange,
}: {
  id: string;
  field: Field;
  value: unknown;
  locked: boolean;
  invalid: { "aria-invalid"?: true; "aria-describedby"?: string };
  onChange: (v: unknown) => void;
}) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const aborter = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const current = parseAttachment(value);

  /**
   * ⚠️ « CONTINUER » PENDANT LE TRANSFERT PARTAIT SANS LE FICHIER. Le champ ne prend
   * sa valeur qu'à la fin ; facultatif, il laissait l'écran passer à la suite avec
   * une réponse vide et un fichier orphelin — sans un mot. Les quatre écrans qui
   * portent un formulaire ont chacun leur `onSubmit` : plutôt que de les instruire
   * tous, le champ retient lui-même l'envoi de SON formulaire tant qu'il transfère,
   * en capture sur l'élément, donc avant le gestionnaire de React, et le dit.
   */
  useEffect(() => {
    if (!busy) return;
    const form = root.current?.closest("form");
    if (!form) return;
    const hold = (e: Event) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      setProblem(t("ATTACHMENT_WAIT", "Attendez la fin du transfert."));
    };
    form.addEventListener("submit", hold, true);
    return () => form.removeEventListener("submit", hold, true);
  }, [busy, t]);

  /* Quitter l'écran annule le transfert : sa réponse n'aurait plus personne à qui
     s'écrire, et le fichier serait orphelin. */
  useEffect(() => () => aborter.current?.abort(), []);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setProblem(null);
    if (!accepts(field.accept, file)) {
      setProblem(t("ATTACHMENT_TYPE_REFUSED", "Ce type de fichier n'est pas accepté."));
      return;
    }
    const control = new AbortController();
    aborter.current = control;
    setBusy(true);
    setSending(file.name);
    try {
      const uuid = await uploadAttachment(file, field.maxMb * 1024 * 1024, control.signal);
      onChange(formatAttachment({ uuid, name: file.name }));
      // Le « attendez » posé par un clic pendant l'envoi n'a plus lieu d'être.
      setProblem(null);
    } catch (e) {
      if (control.signal.aborted) return;
      const mo = Math.round((file.size / 1024 / 1024) * 10) / 10;
      setProblem(
        e instanceof UploadError && e.message === "too-big"
          ? t("ATTACHMENT_TOO_BIG", "Fichier trop lourd ($1 Mo, maximum $2 Mo).", [mo, field.maxMb])
          : t("ATTACHMENT_FAILED", "Le transfert a échoué. Réessayez."),
      );
    } finally {
      if (!control.signal.aborted) {
        setBusy(false);
        if (input.current) input.current.value = "";
      }
    }
  };

  const open = async () => {
    if (!current) return;
    const tab = window.open("", "_blank");
    try {
      const url = await attachmentUrl(current.uuid, current.name);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch {
      tab?.close();
      setProblem(t("ATTACHMENT_FAILED", "Le transfert a échoué. Réessayez."));
    }
  };

  const label = (accept: string) => {
    const fam = accept
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean)
      .map((a) => (a === "image/*" ? "images" : a.startsWith(".") ? a.slice(1).toUpperCase() : a));
    return fam.length ? fam.join(", ") : "";
  };
  const kinds = label(field.accept);
  const hint = [kinds, t("ATTACHMENT_MAX", "Maximum $1 Mo", [field.maxMb])]
    .filter(Boolean)
    .join(" · ");

  const [over, setOver] = useState(false);

  return (
    <div className="fatt" ref={root} aria-busy={busy || undefined} {...invalid}>
      {current || busy ? (
        <div className={`fatt__card${busy ? " fatt__card--busy" : ""}`}>
          <FileIcon name={busy ? sending : (current?.name ?? "")} />
          <div className="fatt__body">
            <span className="fatt__name" title={busy ? sending : current?.name}>
              {busy ? sending : current?.name}
            </span>
            {busy && <small className="fatt__hint">{t("ATTACHMENT_SENDING", "Envoi en cours")}</small>}
            {current && !busy && (
              <button type="button" className="fatt__open" onClick={() => void open()}>
                {t("ATTACHMENT_OPEN", "Ouvrir")}
              </button>
            )}
          </div>
          {!locked && !busy && (
            <div className="fatt__acts">
              <button type="button" className="fatt__act" onClick={() => input.current?.click()}>
                {t("ATTACHMENT_REPLACE", "Remplacer")}
              </button>
              <button type="button" className="fatt__act fatt__act--off" onClick={() => onChange("")}>
                {t("REMOVE", "Retirer")}
              </button>
            </div>
          )}
        </div>
      ) : (
        !locked && (
          /* ⚠️ UN BOUTON, PAS UN LABEL : le clic ouvre le sélecteur, et le glisser-déposer
             est un plus (sur mobile il n'existe pas, le bouton suffit). */
          <button
            type="button"
            className={`fatt__drop${over ? " fatt__drop--over" : ""}`}
            onClick={() => input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              void pick(e.dataTransfer.files?.[0]);
            }}
          >
            <UploadIcon />
            <span className="fatt__cta">
              <b>{t("ATTACHMENT_CHOOSE", "Choisir un fichier")}</b>
              <small>{t("ATTACHMENT_OR_DROP", "ou déposez-le ici")}</small>
            </span>
            {hint && <small className="fatt__hint">{hint}</small>}
          </button>
        )
      )}
      {!locked && (
        <input
          ref={input}
          id={id}
          type="file"
          accept={field.accept || undefined}
          hidden
          onChange={(e) => void pick(e.target.files?.[0])}
        />
      )}
      {problem && (
        <p className="f__err" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

/** L'icône d'un fichier : une « feuille » qui porte son extension, teintée par famille. */
function FileIcon({ name }: { name: string }) {
  const ext = (name.match(/\.([a-z0-9]{1,5})$/i)?.[1] ?? "").toLowerCase();
  const family = /^(pdf)$/.test(ext)
    ? "pdf"
    : /^(png|jpe?g|gif|webp|heic|svg)$/.test(ext)
      ? "img"
      : /^(docx?|xlsx?|pptx?|txt|csv)$/.test(ext)
        ? "doc"
        : "any";
  return (
    <span className={`fatt__ico fatt__ico--${family}`} aria-hidden>
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
        <path d="M14 3v5h5" />
      </svg>
      {ext && <i>{ext.slice(0, 4)}</i>}
    </span>
  );
}

function UploadIcon() {
  return (
    <svg className="fatt__up" viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M12 16V4" />
      <path d="m7 9 5-5 5 5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}
